// Client for the Agent Builder Service (a separate backend: chat -> workflow -> agent instructions).
// It only builds from what the agent runtime offers (its catalogue), and publishes agents the
// runtime then runs. Contract: docs/agent-builder-contract.md in the agent-platform repo.

import { useTypedSelector } from '@circuitry-ai/doc-data';

// The agent platform API (runtime + builder). Set per environment by the deploy workflow; a local
// dev server uses the deployed dev stack (set CTY_AGENT_BUILDER_URL to point it elsewhere).
export const AGENT_BUILDER_URL: string =
  (import.meta.env.CTY_AGENT_BUILDER_URL as string | undefined) ||
  (import.meta.env.DEV ? 'https://asj.dev.circuitry.ai' : '');

// action: calls a tool (an MCP action). ai: the agent does it itself, no tool (e.g. write the
// reply). decision: a yes/no check. handoff: to a person. end: how the run finishes.
export type NodeKind = 'start' | 'action' | 'ai' | 'decision' | 'handoff' | 'end';

export interface NodeData {
  label?: string;
  note?: string;
  trigger?: 'email' | 'chat';
  inputs?: string[];
  subject_contains?: string; // start (email): only emails whose subject contains this
  planned_tool?: string; // action: a tool that does not exist yet (add it as a Circuitry action)
  tool?: string;
  condition?: string;
  reason?: string;
  outcome?: string;
  [key: string]: unknown;
}

export interface WorkflowNode {
  id: string;
  type: NodeKind;
  position: { x: number; y: number };
  data: NodeData;
}

export interface WorkflowEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string | null;
}

export interface ChatTurn {
  role: 'user' | 'assistant';
  text: string;
  at: string | null;
  added: string[];
  changed: string[];
  error: boolean;
  options?: string[]; // the builder asked a question: answers the user can pick
  undo?: boolean; // the workflow can go back to how it was before this turn (POST /undo)
}

export interface Settings {
  name: string;
  title: string;
  role: string;
  goal: string;
  rules: string;
  llm: string;
  max_steps: number;
  output_model: string;
  sample_inputs: Record<string, string>;
}

export interface Workflow {
  version: number;
  blueprint: string | null; // the blueprint this journey is built in, e.g. support_asj
  requirement: string;
  built_from: string | null;
  settings: Settings;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  status: 'draft' | 'published';
  updated_at: string | null;
  published_at: string | null;
  published_hash: string | null;
  chat: ChatTurn[];
  // These are unpublished changes: the saved workflow changes only when they are published.
  unpublished?: boolean;
}

export interface Issue {
  node_id: string | null;
  message: string;
  kind?: 'problem' | 'missing_tool'; // missing_tool: the step is right, its tool must be added first
}

export interface ToolInput {
  name: string;
  type: string;
  description: string;
  required: boolean;
}

export interface ToolInfo {
  name: string;
  display_name: string;
  category: string;
  business_description: string;
  handoff: boolean;
  model_description?: string;
  inputs?: ToolInput[];
  server?: string; // the MCP server that runs it
}

// What a journey can be created as. The builder builds only inside it; the agent runs inside it.
export interface Blueprint {
  id: string;
  version: number;
  ref: string;
  title: string;
  description: string;
  triggers: Record<string, { label?: string; inputs?: string[] }>;
  tools: string[];
  tool_details: ToolInfo[];
  outcomes: Record<string, string>;
  instructions: string;
  rules: { first_tool: Record<string, string>; before: Record<string, string[]>; at_most_once: string[] };
  example_requirement: string;
}

export interface NewAsj {
  title: string;
  llm: string;
  blueprint?: string; // none: a custom journey, no blueprint's rules
}

export interface AgentSummary {
  name: string;
  title: string;
  description: string; // the journey's goal
  blueprint: string | null;
  status: 'draft' | 'published';
  updated_at: string | null;
  published: boolean;
  editable: boolean;
}

export interface SyncStatus {
  requirement: 'in_sync' | 'changed' | 'not_built';
  instructions: 'in_sync' | 'behind' | 'not_published';
  hand_edited: boolean;
}

export interface Preview {
  agent_md: string;
  task_md: string;
  step_numbers: Record<string, number>;
}

export interface GenerateResult {
  workflow: Workflow;
  issues: Issue[];
  step_numbers: Record<string, number>;
  summary: string;
  gaps: string[];
  attempts: number;
  changes: { added: string[]; changed: string[]; removed: string[] };
  clarify: { question: string; options: string[] } | null; // asked instead of building
}

// An email in the support mailbox, to pick for a test run.
export interface InboxEmail {
  message_id: string;
  subject: string;
  from: string;
  received_at: string;
  processed: boolean; // the inbox dashboard already ran on it
}

export interface RunStep {
  id: number;
  tool: string;
  status: 'running' | 'done' | 'failed';
  summary: string;
  args: Record<string, unknown>;
  result: string | null;
}

// What the run streams: the platform's run state (run_flow.RunState), pushed after every step.
export interface RunState {
  status?: 'idle' | 'running' | 'done' | 'failed';
  steps?: RunStep[];
  outcome?: Record<string, unknown> | null;
  error?: string | null;
  message?: string; // the platform's last word, e.g. why it could not start
}

export class BuilderApiError extends Error {
  constructor(message: string, public status: number, public issues: Issue[] = []) {
    super(message);
  }
}

// The signed-in user's Circuitry token: the agent platform API accepts only platform tokens.
// Set by useAgentBuilderAuth() in every page that calls the API (before its effects run).
let authToken: string | null = null;

export function useAgentBuilderAuth(): void {
  authToken = useTypedSelector((state) => state.auth.token) || null;
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  if (!AGENT_BUILDER_URL) throw new BuilderApiError('The agent builder is not configured for this environment (CTY_AGENT_BUILDER_URL).', 0);
  try {
    res = await fetch(`${AGENT_BUILDER_URL}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        ...(init?.headers ?? {}),
      },
    });
  } catch {
    throw new BuilderApiError(`The agent builder service is not reachable at ${AGENT_BUILDER_URL}.`, 0);
  }
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    if (res.status === 401) throw new BuilderApiError('Your session has expired. Sign in again.', 401);
    const detail = body?.detail;
    if (detail && typeof detail === 'object' && Array.isArray(detail.issues)) {
      throw new BuilderApiError(detail.message ?? 'Fix these problems first.', res.status, detail.issues);
    }
    throw new BuilderApiError(typeof detail === 'string' ? detail : `Request failed (${res.status})`, res.status);
  }
  return body as T;
}

const json = (body: unknown) => JSON.stringify(body);
const enc = encodeURIComponent;

// Runs the published agent once, live, over AG-UI (POST /agent, a server-sent event stream).
// Every STATE_SNAPSHOT carries the whole run state, so the page just shows the latest one.
async function runAgent(name: string, inputs: Record<string, string>, onState: (s: RunState) => void, signal?: AbortSignal): Promise<RunState> {
  if (!AGENT_BUILDER_URL) throw new BuilderApiError('The agent builder is not configured for this environment (CTY_AGENT_BUILDER_URL).', 0);
  let res: Response;
  try {
    res = await fetch(`${AGENT_BUILDER_URL}/agent`, {
      method: 'POST',
      signal,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      body: json({
        threadId: crypto.randomUUID(),
        runId: crypto.randomUUID(),
        state: { agent_name: name, inputs },
        messages: [{ id: crypto.randomUUID(), role: 'user', content: `Run ${name}` }],
        tools: [],
        context: [],
        forwardedProps: {},
      }),
    });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new BuilderApiError(`The agent platform is not reachable at ${AGENT_BUILDER_URL}.`, 0);
  }
  if (res.status === 401) throw new BuilderApiError('Your session has expired. Sign in again.', 401);
  if (!res.ok || !res.body) throw new BuilderApiError(`The run could not start (${res.status}).`, res.status);

  let state: RunState = {};
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  const handle = (event: { type?: string; snapshot?: RunState; messages?: { role: string; content?: string }[]; message?: string }) => {
    if (event.type === 'STATE_SNAPSHOT' && event.snapshot) {
      state = { ...event.snapshot, message: state.message };
    } else if (event.type === 'MESSAGES_SNAPSHOT') {
      const last = [...(event.messages ?? [])].reverse().find((m) => m.role === 'assistant');
      if (last?.content) state = { ...state, message: last.content };
    } else if (event.type === 'RUN_ERROR') {
      state = { ...state, status: 'failed', error: event.message ?? 'The run failed.' };
    } else {
      return;
    }
    onState(state);
  };
  for (;;) {
    const { done, value } = await reader.read();
    buffer += decoder.decode(value, { stream: !done });
    const frames = buffer.split(/\r?\n\r?\n/);
    buffer = done ? '' : frames.pop() ?? '';
    for (const frame of frames) {
      const data = frame
        .split(/\r?\n/)
        .filter((l) => l.startsWith('data:'))
        .map((l) => l.slice(5).trimStart())
        .join('\n');
      if (data) {
        try {
          handle(JSON.parse(data));
        } catch {
          // not an event we read
        }
      }
    }
    if (done) return state;
  }
}

export const agentBuilderApi = {
  agents: () => call<AgentSummary[]>('/agents'),
  createAgent: (asj: NewAsj) => call<Workflow>('/agents', { method: 'POST', body: json(asj) }),
  blueprints: () => call<Blueprint[]>('/blueprints'),
  blueprint: (id: string) => call<Blueprint>(`/blueprints/${enc(id)}`),
  workflow: (name: string) => call<Workflow>(`/agents/${enc(name)}`),
  tools: () => call<ToolInfo[]>('/tools'),
  llmProfiles: () => call<string[]>('/llm-profiles'),
  sync: (name: string) => call<SyncStatus>(`/agents/${enc(name)}/sync`),
  preview: (name: string, wf: Workflow) =>
    call<Preview>(`/agents/${enc(name)}/preview`, { method: 'POST', body: json(wf) }),
  // First message builds (no change); later messages change the workflow.
  generate: (name: string, requirement: string, change?: string) =>
    call<GenerateResult>(`/agents/${enc(name)}/generate`, {
      method: 'POST',
      body: json({ requirement, change: change ?? null }),
    }),
  // Back to the workflow as it was before the builder's last change (the chat keeps everything).
  undo: (name: string) => call<GenerateResult>(`/agents/${enc(name)}/undo`, { method: 'POST' }),
  // Drop the unpublished changes: back to the saved workflow (the chat keeps everything).
  discard: (name: string) => call<GenerateResult>(`/agents/${enc(name)}/discard`, { method: 'POST' }),
  saveDraft: (name: string, wf: Workflow) =>
    call<{ workflow: Workflow; issues: Issue[] }>(`/agents/${enc(name)}/draft`, { method: 'PUT', body: json(wf) }),
  publish: (name: string, wf: Workflow) =>
    call<Workflow>(`/agents/${enc(name)}/publish`, { method: 'POST', body: json(wf) }),
  // The agent.md and task.md the agent runs with now (may differ from the workflow until published).
  published: (name: string) => call<{ agent_md: string; task_md: string }>(`/agents/${enc(name)}/published`),
  rename: (name: string, title: string) => call<Workflow>(`/agents/${enc(name)}`, { method: 'PATCH', body: json({ title }) }),
  deleteAgent: (name: string) => call<null>(`/agents/${enc(name)}`, { method: 'DELETE' }),
  // A live test run of the published agent.
  inputs: (name: string) => call<string[]>(`/agents/${enc(name)}/inputs`),
  inbox: (top = 15) => call<InboxEmail[]>(`/inbox?top=${top}`),
  // The emails this journey would take, newest first: its subject filter (agent.md inbox) applied.
  testEmails: (name: string, top = 15) =>
    call<{ subject_contains: string; emails: InboxEmail[] }>(`/agents/${enc(name)}/test-emails?top=${top}`),
  run: runAgent,
};
